// src/client/renderer/vfx/env/dust-motes-pool.ts
//
// Plan 18, Task 3.1 — Dust motes ambient pool.
//
// Behaviour contract:
//   - ALWAYS-ON ambient pool. mode: 'ambient'.
//   - Wire-up site calls manager.spawn('dust_motes', { position: { x: 0, y: 0, z: 0 } })
//     once at scene-build time.
//   - Mesh: a THREE.Points with AMBIENT_TIMING_CONSTANTS.dustMoteCount vertices
//     distributed in a ~20-unit cube around origin. Additive PointsMaterial
//     (warm white 0xfff5cc, size 0.1, sizeAttenuation true).
//   - Animation: update(dt) moves motes on tiny circles (radius ~0.3, period
//     5-9s per mote) using a closed-form sin formula. Each vertex has a
//     deterministic phase derived from its index — no Math.random().
//   - Single-instance pool. isFinished always returns false.
//   - dispose() disposes geometry + material once each.
//
// Implementation notes:
//   - Deterministic phases: phase[i] = i * 0.137 (golden-ratio increment).
//   - Deterministic periods: period[i] = periodMin + (i % 5) * periodStep.
//   - Base positions are spread deterministically using a golden-ratio 3D lattice
//     (no RNG) so vertices are well-distributed in the 20-unit cube.
//   - geometry.attributes.position.needsUpdate = true is set after each update.

import * as THREE from 'three';
import type { EnvCeremonyPool, EnvCeremonySpawnArgs } from './env-ceremony-types';

export const AMBIENT_TIMING_CONSTANTS = {
  dustMoteCount: 120,
  dustMotePeriodMin: 5, // sec
  dustMotePeriodMax: 9, // sec
  dustMoteRadius: 0.3,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Pool config
// ─────────────────────────────────────────────────────────────────────────────

export interface DustMotesPoolConfig {
  readonly scene: THREE.Scene;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Spread of the mote cloud (half-extent in each axis). */
const CLOUD_HALF_EXTENT = 10; // 20-unit cube total

/** PointsMaterial point size (world units with sizeAttenuation). */
const POINT_SIZE = 0.1;

/** Warm-white colour. */
const MOTE_COLOUR = 0xfff5cc;

// ─────────────────────────────────────────────────────────────────────────────
// Deterministic base position lattice
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Build deterministic base positions for N motes using a 3D Halton-like
 * sequence (bases 2, 3, 5) mapped to [-CLOUD_HALF_EXTENT, +CLOUD_HALF_EXTENT].
 * No Math.random() — output is identical on every call.
 */
function halton(index: number, base: number): number {
  let result = 0;
  let f = 1;
  let i = index + 1; // 1-based to avoid trivial 0 at i=0
  while (i > 0) {
    f /= base;
    result += f * (i % base);
    i = Math.floor(i / base);
  }
  return result;
}

function buildBasePositions(count: number): Float32Array {
  const positions = new Float32Array(count * 3);
  for (let i = 0; i < count; i++) {
    // Map [0,1) → [-CLOUD_HALF_EXTENT, +CLOUD_HALF_EXTENT)
    positions[i * 3 + 0] = (halton(i, 2) * 2 - 1) * CLOUD_HALF_EXTENT;
    positions[i * 3 + 1] = (halton(i, 3) * 2 - 1) * CLOUD_HALF_EXTENT;
    positions[i * 3 + 2] = (halton(i, 5) * 2 - 1) * CLOUD_HALF_EXTENT;
  }
  return positions;
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-mote animation parameters (all deterministic from index)
// ─────────────────────────────────────────────────────────────────────────────

function motePhase(index: number): number {
  // Golden-ratio phase increment — well-distributed over [0, 2π)
  return (index * 0.137 * Math.PI * 2) % (Math.PI * 2);
}

function motePeriod(index: number): number {
  const { dustMotePeriodMin, dustMotePeriodMax } = AMBIENT_TIMING_CONSTANTS;
  const step = (dustMotePeriodMax - dustMotePeriodMin) / 5;
  return dustMotePeriodMin + (index % 5) * step;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createDustMotesPool({ scene }: DustMotesPoolConfig): EnvCeremonyPool {
  const count = AMBIENT_TIMING_CONSTANTS.dustMoteCount;

  /** Shared points object — null until first acquire(). */
  let sharedPoints: THREE.Points | null = null;
  let sharedGeometry: THREE.BufferGeometry | null = null;
  let sharedMaterial: THREE.PointsMaterial | null = null;

  /** Base positions (the orbit centres), per-vertex. */
  let basePositions: Float32Array | null = null;

  /** Cumulative elapsed time (seconds), drives the closed-form sin formula. */
  let elapsed = 0;

  let disposed = false;

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(args: EnvCeremonySpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    // Idempotent re-acquire
    if (sharedPoints !== null) return sharedPoints;

    // Build deterministic base positions
    basePositions = buildBasePositions(count);

    sharedGeometry = new THREE.BufferGeometry();
    const posAttr = new THREE.Float32BufferAttribute(basePositions.slice(), 3);
    posAttr.setUsage(THREE.DynamicDrawUsage);
    sharedGeometry.setAttribute('position', posAttr);

    sharedMaterial = new THREE.PointsMaterial({
      color: MOTE_COLOUR,
      size: POINT_SIZE,
      sizeAttenuation: true,
      transparent: true,
      opacity: 0.7,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    sharedPoints = new THREE.Points(sharedGeometry, sharedMaterial);

    // Position from spawn args
    const pos = args.position ?? { x: 0, y: 0, z: 0 };
    sharedPoints.position.set(pos.x, pos.y, pos.z);

    scene.add(sharedPoints);
    return sharedPoints;
  }

  // ── update ─────────────────────────────────────────────────────────────────

  function update(deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    if (disposed || sharedPoints === null || sharedGeometry === null || basePositions === null) {
      return new Set();
    }

    elapsed += deltaSeconds;

    const posAttr = sharedGeometry.attributes['position'] as THREE.BufferAttribute;
    const arr = posAttr.array as Float32Array;
    const { dustMoteRadius } = AMBIENT_TIMING_CONSTANTS;

    for (let i = 0; i < count; i++) {
      const phase = motePhase(i);
      const period = motePeriod(i);
      const angularRate = (2 * Math.PI) / period;

      // Closed-form circular orbit in XZ plane around the base position
      const angle = phase + angularRate * elapsed;
      arr[i * 3 + 0] = basePositions[i * 3 + 0] + Math.cos(angle) * dustMoteRadius;
      arr[i * 3 + 1] = basePositions[i * 3 + 1] + Math.sin(angle * 0.7) * dustMoteRadius * 0.5;
      arr[i * 3 + 2] = basePositions[i * 3 + 2] + Math.sin(angle) * dustMoteRadius;
    }

    posAttr.needsUpdate = true;

    return new Set([sharedPoints]);
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(_emitter: THREE.Object3D): boolean {
    // Always-on ambient pool — never finished
    return false;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    if (sharedPoints === null || emitter !== sharedPoints) return;
    scene.remove(sharedPoints);
    sharedGeometry?.dispose();
    sharedMaterial?.dispose();
    sharedPoints = null;
    sharedGeometry = null;
    sharedMaterial = null;
    basePositions = null;
  }

  // ── dispose (F4) ───────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    if (sharedPoints !== null) {
      scene.remove(sharedPoints);
      sharedGeometry?.dispose();
      sharedMaterial?.dispose();
      sharedPoints = null;
      sharedGeometry = null;
      sharedMaterial = null;
      basePositions = null;
    }
  }

  return { mode: 'ambient', acquire, update, isFinished, release, dispose };
}
